import { checkPermissions, getUserPermissions, permissionsByEntity } from '@cmg/auth';
import { Option } from '@cmg/common';
import sortBy from 'lodash/sortBy';

import { getFeatureToggles } from '../../../config/appSettings';
import { OfferingType } from '../../../graphql/__generated__';

export enum InternalOfferingType {
  IPO_SPACS = 'IPO_SPAC',
  // IPO_SPACS is saved in user's persisted filters, so we still need to support it.
  IPO_SPAC_DEPRECATED = 'IPO_SPACS',
}

export type SupportedOfferingType =
  | OfferingType.Atm
  | OfferingType.Convertible
  | OfferingType.DirectListing
  | OfferingType.FollowOn
  | OfferingType.Ipo
  | OfferingType.MarketedFo
  | OfferingType.OvernightFo
  | OfferingType.RegisteredBlock
  | OfferingType.RegisteredDirect
  | OfferingType.Rights
  | OfferingType.UnregisteredBlock
  | OfferingType.UnderwrittenRd
  | OfferingType.Abb
  | OfferingType.IpoSpac;

export type CalendarOfferingType = OfferingType | InternalOfferingType;

export const CalendarOptions: Record<
  Extract<CalendarOfferingType, SupportedOfferingType>,
  Option<CalendarOfferingType>
> = {
  [OfferingType.Ipo]: { value: OfferingType.Ipo, label: 'IPO' },
  [OfferingType.Rights]: { value: OfferingType.Rights, label: 'Rights' },
  [OfferingType.OvernightFo]: { value: OfferingType.OvernightFo, label: 'Overnight FO' },
  [OfferingType.MarketedFo]: { value: OfferingType.MarketedFo, label: 'Marketed FO' },
  [OfferingType.RegisteredBlock]: {
    value: OfferingType.RegisteredBlock,
    label: 'Registered Block',
  },
  [OfferingType.UnregisteredBlock]: {
    value: OfferingType.UnregisteredBlock,
    label: 'Unregistered Block',
  },
  [OfferingType.FollowOn]: {
    value: OfferingType.FollowOn,
    label: 'Follow On',
  },
  [OfferingType.Convertible]: {
    value: OfferingType.Convertible,
    label: 'Convertible',
  },
  [OfferingType.Atm]: {
    value: OfferingType.Atm,
    label: 'Atm',
  },
  [OfferingType.RegisteredDirect]: {
    value: OfferingType.RegisteredDirect,
    label: 'Registered direct',
  },
  [OfferingType.DirectListing]: {
    value: OfferingType.DirectListing,
    label: 'Direct Listing',
  },
  [OfferingType.UnderwrittenRd]: {
    value: OfferingType.UnderwrittenRd,
    label: 'Underwritten RD',
  },
  [OfferingType.Abb]: {
    value: OfferingType.Abb,
    label: 'ABB',
  },
  [OfferingType.IpoSpac]: {
    value: OfferingType.IpoSpac,
    label: 'IPO (SPACs)',
  },
};

export type FilterValues = {
  offeringType: CalendarOfferingType[];
  sector: string[];
  customSectorId: string[];
  sizeInDollars: RangeFilter;
  marketCap: RangeFilter;
  leftLeadFirmId: string[];
  managerFirmId: string[];
  useCustomSectors: boolean;
  countries: string[];
  aiInterestSummary: string;
};

export type RangeFilter = { min?: number; max?: number };

const offeringTypeSortOrder: Record<
  Extract<CalendarOfferingType, SupportedOfferingType>,
  number
> = {
  [OfferingType.Ipo]: 1,
  [OfferingType.IpoSpac]: 2,
  [OfferingType.DirectListing]: 3,

  [OfferingType.FollowOn]: 4,

  [OfferingType.MarketedFo]: 5,
  [OfferingType.OvernightFo]: 6,
  [OfferingType.Abb]: 7,
  [OfferingType.UnderwrittenRd]: 8,

  [OfferingType.RegisteredBlock]: 9,
  [OfferingType.UnregisteredBlock]: 10,

  [OfferingType.Convertible]: 11,
  [OfferingType.Rights]: 12,

  [OfferingType.Atm]: 13,
  [OfferingType.RegisteredDirect]: 14,
};

const excludedOfferingTypes = [OfferingType.Atm.valueOf(), OfferingType.RegisteredDirect.valueOf()];

export function getAllOfferingOptions(): Option<CalendarOfferingType>[] {
  const userPermissions = getUserPermissions();
  const canViewConverts = checkPermissions(userPermissions, [
    permissionsByEntity.ConvertsOffering.READ,
  ]);
  const canViewRights = checkPermissions(userPermissions, [
    permissionsByEntity.InternationalOffering.READ,
  ]);
  const canViewABB = checkPermissions(userPermissions, [
    permissionsByEntity.InternationalOffering.READ,
  ]);
  const { isNewDirectOfferingFieldsOn: canViewDirects } = getFeatureToggles();

  return sortBy(CalendarOptions, option => offeringTypeSortOrder[option.value]).filter(
    option =>
      !excludedOfferingTypes.includes(option.value) &&
      (canViewConverts || option.value !== OfferingType.Convertible) &&
      (canViewRights || option.value !== OfferingType.Rights) &&
      (canViewABB || option.value !== OfferingType.Abb) &&
      (canViewDirects || option.value !== OfferingType.RegisteredDirect)
  );
}
